import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetGatesContextQuery } from 'modules/gates/service';
import { beginAndSendGateMFA } from 'modules/gates/thunk';
import { mapDealerIdsToNames } from 'modules/dealers/utils';
import {
  Button, PageFooter, PageHeader, SelectInput, Spinner,
} from 'components/ui';
import { PhoneNumber } from 'modules/selfServices/types/SelfService';
import PhoneNumberInput from 'components/ui/PhoneNumberInput';
import { ButtonType } from 'components/ui/Button';
import { ChannelEnum } from 'modules/auth/types/LoginRequest';
import { getSelectedLanguage } from 'modules/translations/selectors';
import { useDispatch, useSelector } from 'hooks';
import { useGetCountriesQuery } from 'modules/refData/service';

const Login: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const language = useSelector(getSelectedLanguage);

  const { data: context, isLoading: isContextLoading, isSuccess } = useGetGatesContextQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: countries } = useGetCountriesQuery({ language });
  const dealersData = React.useMemo(() => mapDealerIdsToNames(context?.dealers), [context?.dealers]);

  const [selectedDealerId, setSelectedDealerId] = React.useState<string>(Object.keys(dealersData ?? {})?.[0]);
  const [phoneNumber, setPhoneNumber] = React.useState<PhoneNumber>({ prefix: '', local: '' });
  const [hasPhoneError, setHasPhoneError] = React.useState(false);
  const [isThunkLoading, setIsThunkLoading] = React.useState(false);

  const isNextDisabled = !selectedDealerId || !phoneNumber?.local || hasPhoneError;
  const hasMultipleDealers = Object.keys(dealersData ?? {}).length > 1;

  React.useEffect(() => {
    if (isSuccess && !selectedDealerId) {
      setSelectedDealerId(Object.keys(dealersData)?.[0]);
    }
  }, [dealersData, isSuccess, selectedDealerId]);

  React.useEffect(() => {
    if (context?.dealers && selectedDealerId && countries) {
      const selectedDealer = context.dealers.find((d) => d.id === selectedDealerId);
      const countryCode = selectedDealer?.dealerCountry?.code;
      const country = countries.find((c) => c.code === countryCode);

      if (country) {
        setPhoneNumber((previousPhoneNumber) => {
          // If the customer has already started typing the phone number, we don't want to change the default prefix.
          if (previousPhoneNumber.local) {
            return previousPhoneNumber;
          }
          return { ...previousPhoneNumber, prefix: country.prefix.toString() };
        });
      }
    }
  }, [context?.dealers, countries, selectedDealerId]);

  const handleChangeDealer = React.useCallback((value: string) => {
    setSelectedDealerId(value);
  }, []);

  const handleNext = React.useCallback(async () => {
    setIsThunkLoading(true);
    await dispatch(beginAndSendGateMFA({
      dealerId: selectedDealerId,
      phoneNumber: `+${phoneNumber.prefix}${phoneNumber.local}`,
      channel: ChannelEnum.SMS,
      language,
    })).unwrap();
    setIsThunkLoading(false);
  }, [dispatch, selectedDealerId, phoneNumber, language]);

  const isLoading = isContextLoading || isThunkLoading;

  return (
    <>
      <PageHeader hideSteps title={<FormattedMessage id="gate.open.title" defaultMessage="Open the gate" />} hidePrevButton />
      <div className="main-content">
        <main className="content flex flex-col gap-6">
          {isContextLoading ? <Spinner /> : (
            <>
              {hasMultipleDealers && (
                <>
                  <div className="w-full">
                    <SelectInput
                      label={intl.formatMessage({ id: 'gate.selectDealership.label', defaultMessage: 'Select a dealership' })}
                      data={dealersData}
                      value={selectedDealerId}
                      onChange={handleChangeDealer}
                      required
                    />
                  </div>
                  <div className="w-full h-px bg-border-default" />
                </>
              )}
              <p className="text-s text-center m-0">
                <FormattedMessage
                  id="gate.auth.phoneInstruction"
                  defaultMessage="Please enter your phone number. We will send you a code to verify your identity before opening the gate."
                />
              </p>
              <PhoneNumberInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                onError={setHasPhoneError}
                required
                className="w-full"
              />
            </>
          )}
        </main>
        <PageFooter hideNextButton>
          <Button
            type={ButtonType.TERTIARY}
            onClick={handleNext}
            disabled={isNextDisabled}
            loading={isLoading}
            className="footer-button"
          >
            <FormattedMessage id="steps.next" defaultMessage="Next" />
          </Button>
        </PageFooter>
      </div>
    </>
  );
};

export default Login;
